import { jsxs, jsx } from 'react/jsx-runtime';
import { isHotkey } from 'is-hotkey';
import React__default, { useEffect, useMemo } from 'react';
import { shallow as shallow$1 } from '../../../../node_modules/zustand/esm/shallow.js';
import SpaceClick from './SpaceClick.js';
import SpaceCursor from './SpaceCursor.js';
import { styled } from 'styled-components';
import { useSpaceLiveblocksStore } from '../../../multiplayer/config/spaces.config.js';
import { CursorMode } from '../../../types/presence.js';
import { getUserColorFromId, getTextColor } from '@fabric-app/utils/color';
import { useExpandedFdocContentContext } from '../../../resource-detail/components/ExpandedFdoc/ExpandedFdocContentContext.js';

const Main = styled.div.withConfig({
  displayName: "SpaceCursors__Main",
  componentId: "sc-92829038-0"
})([
  `position:absolute;inset:0;pointer-events:none;z-index:1000;`
]);
const ChatWrapper = styled.div.withConfig({
  displayName: "SpaceCursors__ChatWrapper",
  componentId: "sc-92829038-1"
})([
  `position:absolute;transition:transform 0.1s ease-in-out;border:1.5px solid white;border-radius:16px;border-top-left-radius:0;min-height:23px;max-width:200px;width:fit-content;padding:4px 8px;font-size:13.8px;font-style:normal;font-weight:500;> div{overflow:hidden;white-space:nowrap;text-overflow:ellipsis;}> input{background:none;color:inherit;border:none;outline:none;width:100%;height:100%;font-size:inherit;font-style:inherit;font-weight:inherit;}`
]);
const SpaceCursors = ({ resourceId, bodyContext }) => {
  const { itemPreviewContentBodyRef } = useExpandedFdocContentContext();
  const [chatInputFocused, setChatInputFocused] = React__default.useState(false);
  const { others, clickEvents, setClickEvents, broadcastClickEvent, setCursor, userId } = useSpaceLiveblocksStore((state) => ({
    userId: state.userId,
    setCursor: state.setCursor,
    others: state.liveblocks.others,
    clickEvents: state.clickEvents,
    setClickEvents: state.setClickEvents,
    broadcastClickEvent: state.broadcastClickEvent
  }), shallow$1);
  const cursorMode = useSpaceLiveblocksStore((state) => state.cursor.state.mode, shallow$1);
  const cursorState = useSpaceLiveblocksStore((state) => state.cursor.state.mode === CursorMode.Chat ? {
    x: state.cursor.position.x,
    y: state.cursor.position.y,
    message: state.cursor.state.message,
    previousMessage: state.cursor.state.previousMessage
  } : null, shallow$1);
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!isHotkey("mod+/", {
        byKey: true
      })(e) && !isHotkey("mod+shift+/", {
        byKey: true
      })(e)) return;
      if (cursorMode === CursorMode.Chat && chatInputFocused) return;
      e.preventDefault();
      setCursor((cursor) => ({
        ...cursor,
        state: {
          mode: CursorMode.Chat,
          previousMessage: null,
          message: ""
        }
      }));
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [
    chatInputFocused,
    cursorMode,
    setCursor
  ]);
  useEffect(() => {
    const onMouseClick = (e) => {
      if (!itemPreviewContentBodyRef) return;
      const rect = itemPreviewContentBodyRef.getBoundingClientRect();
      const normalizedX = (e.clientX - rect.left + itemPreviewContentBodyRef.scrollLeft) / itemPreviewContentBodyRef.scrollWidth;
      const normalizedY = (e.clientY - rect.top + itemPreviewContentBodyRef.scrollTop) / itemPreviewContentBodyRef.scrollHeight;
      if (!broadcastClickEvent || !resourceId) return;
      broadcastClickEvent(resourceId, {
        x: normalizedX,
        y: normalizedY
      });
    };
    itemPreviewContentBodyRef?.addEventListener("click", onMouseClick);
    return () => {
      itemPreviewContentBodyRef?.removeEventListener("click", onMouseClick);
    };
  }, [
    broadcastClickEvent,
    resourceId,
    itemPreviewContentBodyRef
  ]);
  const color = getUserColorFromId(userId);
  const textColor = getTextColor(color);
  const cursorX = useMemo(() => {
    if (!cursorState?.x) return 0;
    return cursorState?.x * (bodyContext.scrollWidth ?? 0) + 30;
  }, [
    bodyContext.scrollWidth,
    cursorState?.x
  ]);
  const cursorY = useMemo(() => {
    if (!cursorState?.y) return 0;
    return cursorState?.y * (bodyContext.scrollHeight ?? 0) + 30;
  }, [
    bodyContext.scrollHeight,
    cursorState?.y
  ]);
  const chatCursorTimeout = React__default.useRef(void 0);
  useEffect(() => {
    setCursor((cursor) => ({
      ...cursor,
      position: {
        x: 0,
        y: 0,
        pressure: null
      },
      state: {
        mode: CursorMode.Normal,
        type: "default"
      }
    }));
  }, [
    resourceId,
    setCursor
  ]);
  const onInputChange = (e) => {
    setChatInputFocused(true);
    if (e.target.value.length > 50) return;
    setCursor((cursor) => ({
      ...cursor,
      state: {
        ...cursor.state,
        message: e.target.value
      }
    }));
  };
  const onInputKeyDown = (e) => {
    if (cursorMode !== CursorMode.Chat) return;
    switch (e.key) {
      case "Enter": {
        e.preventDefault();
        setCursor((cursor) => cursor.state.mode === CursorMode.Chat ? {
          ...cursor,
          state: {
            ...cursor.state,
            mode: CursorMode.Chat,
            previousMessage: cursor.state.message,
            message: ""
          }
        } : cursor);
        break;
      }
      case "Escape": {
        e.preventDefault();
        e.stopPropagation();
        setCursor((cursor) => ({
          ...cursor,
          state: {
            mode: CursorMode.Normal,
            type: "default"
          }
        }));
        break;
      }
      default: {
        return;
      }
    }
  };
  const onInputBlur = (_e) => {
    setChatInputFocused(false);
  };
  const onInputFocus = (_e) => {
    setChatInputFocused(true);
  };
  useEffect(() => {
    if (chatInputFocused) return;
    clearTimeout(chatCursorTimeout.current);
    chatCursorTimeout.current = window.setTimeout(() => {
      setCursor((cursor) => ({
        ...cursor,
        state: {
          mode: CursorMode.Normal,
          type: "default"
        }
      }));
    }, 5e3);
    return () => {
      clearTimeout(chatCursorTimeout.current);
    };
  }, [
    chatInputFocused,
    cursorMode,
    setCursor
  ]);
  const inactivityTimeout = React__default.useRef(void 0);
  const message = cursorMode === CursorMode.Chat ? cursorState?.message : "";
  useEffect(() => {
    if (cursorMode !== CursorMode.Chat) return;
    clearTimeout(inactivityTimeout.current);
    inactivityTimeout.current = window.setTimeout(() => {
      setCursor((cursor) => ({
        ...cursor,
        state: {
          mode: CursorMode.Normal,
          type: "default"
        }
      }));
    }, 1e4);
    return () => {
      clearTimeout(inactivityTimeout.current);
    };
  }, [
    cursorMode,
    message,
    setCursor
  ]);
  useEffect(() => {
    const onBlur = () => {
      setChatInputFocused(false);
    };
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("blur", onBlur);
    };
  }, []);
  if (!resourceId) return null;
  return /* @__PURE__ */ jsxs(Main, {
    children: [
      cursorMode === CursorMode.Chat && /* @__PURE__ */ jsxs(ChatWrapper, {
        style: {
          transform: `translate(${cursorX}px, ${cursorY}px)`,
          background: color,
          color: textColor
        },
        children: [
          cursorState?.previousMessage && /* @__PURE__ */ jsx("div", {
            children: cursorState.previousMessage
          }),
          /* @__PURE__ */ jsx("input", {
            autoFocus: true,
            onKeyDown: onInputKeyDown,
            onChange: onInputChange,
            onBlur: onInputBlur,
            onFocus: onInputFocus,
            value: cursorState?.message ?? ""
          })
        ]
      }),
      others.filter((other) => other.presence.resourceId === resourceId).map((user) => /* @__PURE__ */ jsx(SpaceCursor, {
        user,
        bodyContext
      }, user.id)),
      clickEvents.map((event, i) => /* @__PURE__ */ jsx(SpaceClick, {
        event,
        bodyContext,
        dismiss: () => {
          setClickEvents((events) => events.filter((e) => e.id !== event.id));
        }
      }, i))
    ]
  });
};

export { SpaceCursors as default };
//# sourceMappingURL=SpaceCursors.js.map
